<template>
  <div class="relative action-filter-dropdown flex-shrink-0">
    <button ref="buttonEl" type="button" @click="toggleOpen" @keydown="handleKeydown"
      class="h-full flex items-center gap-1.5 pl-3 pr-2.5 py-1.5 text-sm border rounded-lg transition-colors cursor-pointer"
      :class="selected.length
        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
        : 'text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1C2128] border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'">
      <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.62a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"/>
      </svg>
      <span class="whitespace-nowrap">Action{{ selected.length ? ` (${selected.length})` : '' }}</span>
      <svg class="w-3.5 h-3.5 flex-shrink-0 transition-transform" :class="open ? 'rotate-180' : ''"
        fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
      </svg>
    </button>

    <!-- Teleported + fixed so the panel escapes this toolbar's own overflow
         context, matching CategoryFilterButton's own dropdown. -->
    <Teleport to="body">
      <div v-if="open" class="fixed inset-0 z-[9998]" @click="open = false"></div>
      <div v-if="open" :style="panelStyle"
        class="fixed z-[9999] bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700 rounded-lg shadow-lg py-1.5 max-h-80 overflow-hidden flex flex-col">
        <div class="px-3.5 pb-1.5 flex-shrink-0 flex items-center justify-between gap-2">
          <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Filter by action</p>
          <button v-if="selected.length" type="button" @click="$emit('clear')"
            class="text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 cursor-pointer flex-shrink-0">
            Clear
          </button>
        </div>
        <div class="overflow-y-auto">
          <button v-for="(action, index) in actions" :key="action" type="button"
            :ref="(el) => setOptionRef(el, index)"
            @click="$emit('toggle', action)" @mouseenter="activeIndex = index"
            :class="index === activeIndex ? 'bg-gray-50 dark:bg-gray-800' : ''"
            class="w-full flex items-center gap-2.5 text-left px-3.5 py-2 transition-colors cursor-pointer">
            <span class="w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center transition-colors"
              :class="isSelected(action) ? 'bg-blue-600 border-blue-600' : 'border-gray-300 dark:border-gray-600'">
              <svg v-if="isSelected(action)" class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
              </svg>
            </span>
            <span class="flex-1 min-w-0 text-sm truncate" :class="isSelected(action) ? 'font-medium text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-200'">
              {{ actionLabel(action) }}
            </span>
          </button>
          <p v-if="actions.length === 0" class="px-3.5 py-4 text-xs text-gray-400 dark:text-gray-500 text-center">
            No activity recorded yet.
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { actionLabel } from 'controllers/vue_components/organizations/activity_logs/activityLogOptions.js'

const props = defineProps({
  // Distinct action values this organization has ever logged (see
  // useActivityLogsStore#fetchAvailableActions) — not a fixed frontend list,
  // so the dropdown never offers a choice that couldn't return anything.
  actions: { type: Array, required: true },
  selected: { type: Array, required: true },
})

const emit = defineEmits(['toggle', 'clear'])

const open = ref(false)
const activeIndex = ref(-1)
const optionRefs = ref([])
const buttonEl = ref(null)
const panelStyle = ref({})

function isSelected(value) {
  return props.selected.includes(value)
}

function setOptionRef(el, index) {
  if (el) optionRefs.value[index] = el
}

function positionPanel() {
  const rect = buttonEl.value.getBoundingClientRect()
  const width = Math.max(rect.width, 240)
  const overflowsRight = rect.left + width > window.innerWidth - 8
  const left = overflowsRight ? Math.max(8, rect.right - width) : rect.left
  return {
    top: `${rect.bottom + 6}px`,
    left: `${left}px`,
    width: `${width}px`,
  }
}

function toggleOpen() {
  const opening = !open.value
  if (opening) panelStyle.value = positionPanel()
  open.value = opening
  activeIndex.value = -1
}

watch(() => props.actions, () => {
  activeIndex.value = -1
  optionRefs.value = []
})

watch(activeIndex, (index) => {
  if (index >= 0) optionRefs.value[index]?.scrollIntoView({ block: 'nearest' })
})

function handleKeydown(e) {
  if (!open.value) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleOpen()
    }
    return
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (props.actions.length) activeIndex.value = (activeIndex.value + 1) % props.actions.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (props.actions.length) {
      activeIndex.value = activeIndex.value <= 0 ? props.actions.length - 1 : activeIndex.value - 1
    }
  } else if (e.key === 'Escape') {
    open.value = false
  } else if (e.key === 'Enter' && activeIndex.value >= 0) {
    e.preventDefault()
    emit('toggle', props.actions[activeIndex.value])
  }
}
</script>
